"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // Use crypto.randomUUID() → removes hydration mismatch from random numbers
  const addToast = (message, type = "default") => {
    const id = crypto.randomUUID();

    setToasts((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  };

  const toastAPI = {
    success: (msg) => addToast(msg, "success"),
    error: (msg) => addToast(msg, "error"),
    loading: (msg) => addToast(msg, "loading"),
  };

  return (
    <ToastContext.Provider value={toastAPI}>
      {children}
      <ToastContainer toasts={toasts} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

/* ---------------------------------------------------
   🚀 Hydration-Safe Toast Container
   - Renders NOTHING on server
   - Renders portal ONLY after client hydration
--------------------------------------------------- */
function ToastContainer({ toasts }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true); // Only mount portal after hydration
  }, []);

  if (!mounted) return null; // Prevent hydration mismatch 100%

  return createPortal(
    <div className="custom-toast-container">
      {toasts.map((toast) => (
        <div key={toast.id} className={`custom-toast custom-toast-${toast.type}`}>
          <div className="custom-toast-icon">
            {toast.type === "success" && <CheckCircle size={20} color="#22c55e" />}
            {toast.type === "error" && <XCircle size={20} color="#ef4444" />}
            {toast.type === "loading" && (
              <Loader2 size={20} className="animate-spin" color="#3b82f6" />
            )}
          </div>
          <div className="custom-toast-message">{toast.message}</div>
        </div>
      ))}

      <style jsx>{`
        .custom-toast-container {
          position: fixed;
          top: 1rem;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          z-index: 9999;
        }

        .custom-toast {
          min-width: 250px;
          max-width: 400px;
          display: flex;
          align-items: center;
          background: #ffffff;
          color: #333;
          padding: 12px 16px;
          border-radius: 8px;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
          opacity: 0;
          transform: translateY(-40px);
          animation: slideDown 0.4s forwards, fadeOut 0.3s forwards 2.7s;
        }

        .custom-toast-icon {
          margin-right: 8px;
          display: flex;
          align-items: center;
        }

        .custom-toast-message {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
        }

        @keyframes slideDown {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeOut {
          to {
            opacity: 0;
            transform: translateY(-40px);
          }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>,
    document.body
  );
}
